import Vue from 'vue'
import VueResource from 'vue-resource'
import Router from 'vue-router'
import LoginPage from '@/pages/login'
import ListingPage from '@/pages/listing'
import ItemFormPage from '@/components/Engine/ApiItemForm'
import vueAuthInstance from '@/services/auth'

import store from '@/store/index'
import NProgress from 'nprogress'

// Rutas con carga diferida: un chunk por área funcional del checklist de
// verificación (docs/checklist-verificacion.md). Solo login y el motor CRUD
// genérico (listing / ApiItemForm) quedan estáticos en el chunk
// principal porque se usan en toda sesión.

// 1. Login y perfil
const PwdResetEmail = () => import(/* webpackChunkName: "profile" */ '@/pages/password_email')
const PwdReset = () => import(/* webpackChunkName: "profile" */ '@/pages/password_reset')
const ProfileEditPage = () => import(/* webpackChunkName: "profile" */ '@/pages/profile_edit')
const ProfileChangePasswordPage = () => import(/* webpackChunkName: "profile" */ '@/pages/profile_change_password')
const ProfileInvoicingDataPage = () => import(/* webpackChunkName: "profile" */ '@/pages/profile_invoicing_data')
const ProfileBackofficePreferencesPage = () => import(/* webpackChunkName: "profile" */ '@/pages/profile_preferences')
const ProfileAddressesPage = () => import(/* webpackChunkName: "profile" */ '@/pages/profile_addresses')

// 3. Shippings bespoke (incluye la landing post-login /list/shippings/manual)
const ShippingListManual = () => import(/* webpackChunkName: "shippings" */ '@/components/ShippingListManual')
const ViewShipping = () => import(/* webpackChunkName: "shippings" */ '@/pages/view_shipping')
const ShippingsSummary = () => import(/* webpackChunkName: "shippings" */ '@/pages/summary')
const CompanyNewShippingPage = () => import(/* webpackChunkName: "shippings" */ '@/pages/company_new')

// 4. Paneles de control
const ControlPanel = () => import(/* webpackChunkName: "panels" */ '@/pages/control_panel')
const ControlPanelCod = () => import(/* webpackChunkName: "panels" */ '@/pages/control_panel_cod')
const IncidencesBy = () => import(/* webpackChunkName: "panels" */ '@/pages/incidences_by')
const FlexesControl = () => import(/* webpackChunkName: "panels" */ '@/pages/flexes_control')
const TransactionList = () => import(/* webpackChunkName: "panels" */ '@/components/Transactions/List')
const IncidenceList = () => import(/* webpackChunkName: "panels" */ '@/components/IncidenceList')

// 5. Warehouse monitors
const WhEntryMonitor = () => import(/* webpackChunkName: "monitors" */ '@/pages/wh_entry_monitor')
const WhExitMonitor = () => import(/* webpackChunkName: "monitors" */ '@/pages/wh_exit_monitor')
const WhIncidencesMonitor = () => import(/* webpackChunkName: "monitors" */ '@/pages/wh_incidences_monitor')
const ToursControlMonitor = () => import(/* webpackChunkName: "monitors" */ '@/pages/tours_control_monitor')
const IncidencesInWarehouse = () => import(/* webpackChunkName: "monitors" */ '@/pages/incidences_in_warehouse')

// 6. Tours y flota
const TourList = () => import(/* webpackChunkName: "tours" */ '@/components/Tours/List')
const ViewTour = () => import(/* webpackChunkName: "tours" */ '@/pages/view_tour')
const FleetRentalOrderList = () => import(/* webpackChunkName: "tours" */ '@/components/Fleet/List')
const FleetRentalOrderEdit = () => import(/* webpackChunkName: "tours" */ '@/components/FleetRentalOrderEdit')
const VehicleCapacity = () => import(/* webpackChunkName: "tours" */ '@/pages/vehicle_capacity')

// 7. Cotizaciones y pricing
const NewQuote = () => import(/* webpackChunkName: "quotes" */ '@/pages/new_quote')
const EditQuote = () => import(/* webpackChunkName: "quotes" */ '@/pages/edit_quote')
const BulkQuotes = () => import(/* webpackChunkName: "quotes" */ '@/pages/bulk_quotes')
const NewQuoteServices = () => import(/* webpackChunkName: "quotes" */ '@/pages/new_quote_services')
const EditQuoteServices = () => import(/* webpackChunkName: "quotes" */ '@/pages/edit_quote_services')
const BulkQuoteProviders = () => import(/* webpackChunkName: "quotes" */ '@/pages/bulk_quote_providers')
const BulkQuote3p = () => import(/* webpackChunkName: "quotes" */ '@/pages/bulk_quote_3p')
const FixedPriceList = () => import(/* webpackChunkName: "quotes" */ '@/components/FixedPrices/List')

// 8. Empresas, usuarios y accesos
const ViewCompany = () => import(/* webpackChunkName: "companies" */ '@/pages/view_company')
const NewCompany = () => import(/* webpackChunkName: "companies" */ '@/pages/new_company')
const UserList = () => import(/* webpackChunkName: "companies" */ '@/components/Users/List')
const ViewUser = () => import(/* webpackChunkName: "companies" */ '@/pages/view_user')
const CourierList = () => import(/* webpackChunkName: "companies" */ '@/components/Couriers/List')
const NewCourier = () => import(/* webpackChunkName: "companies" */ '@/pages/new_courier')
const EditBranding = () => import(/* webpackChunkName: "companies" */ '@/pages/view_branding')
const ViewRole = () => import(/* webpackChunkName: "companies" */ '@/pages/view_role')

// 9. Geografía y mapa
const MapPage = () => import(/* webpackChunkName: "geo" */ '@/pages/map')
const EditCountry = () => import(/* webpackChunkName: "geo" */ '@/components/CountryEdit')
const RegionEditPage = () => import(/* webpackChunkName: "geo" */ '@/components/RegionEdit')
const NewArea = () => import(/* webpackChunkName: "geo" */ '@/pages/new_area')
const ViewArea = () => import(/* webpackChunkName: "geo" */ '@/pages/view_area')
const NewRoutingZone = () => import(/* webpackChunkName: "geo" */ '@/pages/new_routing_zone')
const ViewRoutingZone = () => import(/* webpackChunkName: "geo" */ '@/pages/view_routing_zone')
const NewWarehouse = () => import(/* webpackChunkName: "geo" */ '@/pages/new_warehouse')
const ViewWarehouse = () => import(/* webpackChunkName: "geo" */ '@/pages/view_warehouse')

// 10. AI tools
const AiAddressReviewer = () => import(/* webpackChunkName: "ai" */ '@/pages/ai_address_reviewer')
const AiNegotiator = () => import(/* webpackChunkName: "ai" */ '@/pages/ai_negotiator')
const AiBulkAddressReview = () => import(/* webpackChunkName: "ai" */ '@/pages/ai_bulk_address_review')

// 11. Misceláneas
const DownloadFromApi = () => import(/* webpackChunkName: "misc" */ '@/pages/download_from_api')
const NewEcosystem = () => import(/* webpackChunkName: "misc" */ '@/pages/new_ecosystem')
const EventList = () => import(/* webpackChunkName: "misc" */ '@/components/Events/List')
const PendingAccountingNotesList = () => import(/* webpackChunkName: "misc" */ '@/components/Accounting/List')
const NewItemPacks = () => import(/* webpackChunkName: "misc" */ '@/components/ItemPacksNew')

Vue.use(VueResource)
Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      redirect: '/list/shippings/manual'
    },
    {
      path: '/download/:url',
      name: 'downloadFromApi',
      component: DownloadFromApi,
      meta: { auth: true, title: 'New' },
      props: true
    },
    {
      path: '/ai/test-addresses',
      name: 'AiAddressReviewer',
      component: AiAddressReviewer,
      meta: { auth: true, title: 'New' },
      props: true
    },
    {
      path: '/ai/test-negotiation',
      name: 'AiNegotiator',
      component: AiNegotiator,
      meta: { auth: true, title: 'Negotiation v2 (JSON Mode)' },
      props: true
    },
    {
      path: '/ai/bulk-address-review',
      name: 'AiBulkAddressReview',
      component: AiBulkAddressReview,
      meta: { auth: true, title: 'Bulk Address Review' },
      props: true
    },
    {
      path: '/download/:url/:badLinkPart2/:badLinkPart3',
      name: 'downloadFromApiUrlRedirect',
      component: DownloadFromApi,
      meta: { auth: true, title: 'Download' },
      props: true
    },
    {
      path: '/download',
      name: 'downloadFromApiQuerystring',
      component: DownloadFromApi,
      meta: { auth: true, title: 'Download' }
    },
    {
      path: '/list/couriers',
      name: 'courierList',
      component: CourierList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/list/events',
      name: 'eventList',
      component: EventList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/list/fixed-prices',
      name: 'fixedPriceList',
      component: FixedPriceList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/list/pending-accounting-notes',
      name: 'pendingAccountingNotes',
      component: PendingAccountingNotesList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/summary',
      name: 'shippingsSummary',
      component: ShippingsSummary,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/control',
      name: 'controlPanel',
      component: ControlPanel,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/cod-control',
      name: 'controlPanelCod',
      component: ControlPanelCod,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/incidences-by',
      name: 'incidencesBy',
      component: IncidencesBy,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/flexes-control',
      name: 'flexesControl',
      component: FlexesControl,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/transactions',
      name: 'transactions',
      component: TransactionList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/incidences',
      name: 'incidences',
      component: IncidenceList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/incidences-in-warehouse',
      name: 'incidencesInWarehouse',
      component: IncidencesInWarehouse,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/monitor/entry',
      name: 'whEntryMonitor',
      component: WhEntryMonitor,
      meta: { fakeAuth: true, title: 'List' }
    },
    {
      path: '/monitor/exit',
      name: 'whExitMonitor',
      component: WhExitMonitor,
      meta: { fakeAuth: true, title: 'List' }
    },
    {
      path: '/monitor/incidences',
      name: 'whIncidencesMonitor',
      component: WhIncidencesMonitor,
      meta: { fakeAuth: true, title: 'List' }
    },
    {
      path: '/monitor/tours',
      name: 'toursControlMonitor',
      component: ToursControlMonitor,
      meta: { fakeAuth: true, title: 'List' }
    },
    {
      path: '/new/couriers',
      name: 'courierCreate',
      component: NewCourier,
      meta: { auth: true, title: 'New' },
      props: true
    },
    {
      path: '/edit/branding',
      name: 'EditBranding',
      component: EditBranding,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/quotes',
      name: 'quoteCreate',
      component: NewQuote,
      meta: { auth: true, title: 'New' },
      props: true
    },
    {
      path: '/bulk/quotes',
      name: 'quoteBulk',
      component: BulkQuotes,
      meta: { auth: true, title: 'Bulk' },
      props: true
    },
    {
      path: '/edit/quotes/:id',
      name: 'editQuote',
      component: EditQuote,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/quote-services',
      name: 'quoteServicesCreate',
      component: NewQuoteServices,
      meta: { auth: true, title: 'New' },
      props: true
    },
    {
      path: '/edit/quote-services/:id',
      name: 'editQuoteServices',
      component: EditQuoteServices,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/bulk/quote-providers',
      name: 'quoteProvidersBulk',
      component: BulkQuoteProviders,
      meta: { auth: true, title: 'Bulk' },
      props: true
    },
    {
      path: '/bulk/outsourcing-quotes',
      name: 'BulkQuote3p',
      component: BulkQuote3p,
      meta: { auth: true, title: 'Bulk' },
      props: true
    },
    {
      path: '/edit/countries/:id',
      name: 'editCountry',
      component: EditCountry,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/edit/regions/:id',
      name: 'editRegion',
      component: RegionEditPage,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/login',
      name: 'login',
      component: LoginPage,
      meta: { auth: false, title: 'Log in to your account' }
    },
    {
      path: '/profile/edit',
      name: 'profile-edit',
      component: ProfileEditPage,
      meta: { auth: true, title: 'Edit profile' }
    },
    {
      path: '/profile/change-password',
      name: 'profile-change-password',
      component: ProfileChangePasswordPage,
      meta: { auth: true, title: 'Change Password' }
    },
    {
      path: '/profile/invoicing-data',
      name: 'profile-invoicing-data',
      component: ProfileInvoicingDataPage,
      meta: { auth: true, title: 'Manage Invoicing Data' }
    },
    {
      path: '/profile/preferences',
      name: 'profile-preferences',
      component: ProfileBackofficePreferencesPage,
      meta: { auth: true, title: 'Manage your Backoffice Preferences' }
    },
    {
      path: '/profile/addresses',
      name: 'profile-addresses',
      component: ProfileAddressesPage,
      meta: { auth: true, title: 'Manage Addresses' }
    },
    // {
    //   path: '/users/',
    //   name: 'users',
    //   component: ListingPage,
    //   meta: { auth: true, title: 'Users' },
    //   props: { type: 'users' }
    // },
    {
      path: '/list/tours',
      name: 'tourList',
      component: TourList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/edit/tours/:id',
      name: 'viewTour',
      component: ViewTour,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/edit/companies/:id',
      name: 'viewCompany',
      component: ViewCompany,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/companies/',
      name: 'newCompany',
      component: NewCompany,
      meta: { auth: true, title: 'New' }
    },
    {
      path: '/list/shippings/manual',
      name: 'shippingListManual',
      component: ShippingListManual,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/list/shippings', // DEPRECATED
      name: 'shippingList',
      redirect: '/list/shippings/manual',
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/edit/shippings/:id',
      name: 'viewShipping',
      component: ViewShipping,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/list/users',
      name: 'userList',
      component: UserList,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/list/vehicle-capacity',
      name: 'vehicleCapacityList',
      component: VehicleCapacity,
      meta: { auth: true, title: 'List' }
    },
    {
      path: '/edit/users/:id',
      name: 'viewUser',
      component: ViewUser,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/item-packs',
      name: 'newItemPacks',
      component: NewItemPacks,
      meta: { auth: true, title: 'Create' },
      props: true
    },
    {
      path: '/new/warehouses',
      name: 'newWarehouse',
      component: NewWarehouse,
      meta: { auth: true, title: 'Create' },
      props: true
    },
    {
      path: '/edit/warehouses/:id',
      name: 'viewWarehouse',
      component: ViewWarehouse,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/areas',
      name: 'newArea',
      component: NewArea,
      meta: { auth: true, title: 'Create' },
      props: true
    },
    {
      path: '/edit/areas/:id',
      name: 'viewArea',
      component: ViewArea,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/routing-zones',
      name: 'newRoutingZone',
      component: NewRoutingZone,
      meta: { auth: true, title: 'Create' },
      props: true
    },
    {
      path: '/edit/routing-zones/:id',
      name: 'viewRoutingZone',
      component: ViewRoutingZone,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/edit/roles/:id',
      name: 'viewRole',
      component: ViewRole,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/fleet-rental-orders',
      name: 'FleetRentalOrderNew',
      component: FleetRentalOrderEdit,
      meta: { auth: true, title: 'New' },
      props: true
    },
    {
      path: '/edit/fleet-rental-orders/:id',
      name: 'FleetRentalOrderEdit',
      component: FleetRentalOrderEdit,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/list/fleet-rental-orders',
      name: 'FleetRentalOrderList',
      component: FleetRentalOrderList,
      meta: { auth: true, title: 'List' },
      props: true
    },
    {
      path: '/new/ecosystems',
      name: 'newEcosystem',
      component: NewEcosystem,
      meta: { auth: true, title: 'New Ecosystem' }
    },
    {
      path: '/list/:type',
      name: 'modelList',
      component: ListingPage,
      meta: { auth: true, title: 'List' },
      props: true
    },
    {
      path: '/edit/:type/:id',
      name: 'modelEdit',
      component: ItemFormPage,
      meta: { auth: true, title: 'Edit' },
      props: true
    },
    {
      path: '/new/:type',
      name: 'modelCreate',
      component: ItemFormPage,
      meta: { auth: true, title: 'New' },
      props: true
    },
    {
      path: '/shippings/',
      name: 'shippings',
      component: ListingPage,
      meta: { auth: true, title: 'Shippings' },
      props: { type: 'shippings' }
    },
    {
      path: '/map/',
      name: 'map',
      component: MapPage,
      meta: { auth: true, title: 'Map' }
    },
    {
      path: '/new-company-shipping',
      name: 'newCompanyShipping',
      component: CompanyNewShippingPage,
      meta: { auth: true, title: 'New shipping' }
    },
    {
      path: '/password/reset',
      name: 'password_email',
      component: PwdResetEmail,
      meta: { auth: false, title: 'Forgot Password' }
    },
    {
      name: 'password_reset',
      path: '/password/reset/:token',
      component: PwdReset,
      props: true,
      meta: { auth: false, title: 'Reset Password' }
    }
  ]
})

// loadBar start
router.beforeResolve((to, from, next) => {
  if (to.name) {
    NProgress.start()
    NProgress.set(0.1)
    NProgress.set(0.4)
  }
  next()
})
// loadBar end
router.afterEach((to, from) => {
  window.sessionStorage.removeItem('chunk-reload')
  setTimeout(() => NProgress.done(), 500)
})

// Tras un deploy, un index.html cacheado puede pedir chunks con hash viejo ya
// borrados del bucket: recargar una única vez toma el index.html nuevo; la
// marca en sessionStorage (limpiada al navegar con éxito) evita un loop de
// recargas si el error persiste.
router.onError(error => {
  if (/loading (css )?chunk .* failed/i.test(error.message)) {
    if (!window.sessionStorage.getItem('chunk-reload')) {
      window.sessionStorage.setItem('chunk-reload', '1')
      window.location.reload()
    }
  }
})

router.beforeEach(function (to, from, next) {
  if (to.meta && (to.meta.auth !== undefined || to.meta.fakeAuth)) {
    if (to.meta.auth || to.meta.fakeAuth) {
      if (vueAuthInstance.isAuthenticated()) {
        next()
      } else {
        store.commit('setUid', null)
        store.commit('setEmail', null)
        store.commit('setProfile', {})
        router.push({ name: 'login', query: { redirect: to.fullPath } })
      }
    } else {
      if (vueAuthInstance.isAuthenticated()) {
        router.push({ name: 'summary' })
      } else {
        next()
      }
    }
  } else {
    next()
  }
})

export default router
